import type { OrderSummaryResponse } from '@thinkalpha/locates-ws-client/contracts/orders.js';
import type { RevenueAnalyticsRawRow } from 'src/admin/LAB/RevenueAnalytics/types';
import { lapi as axios } from 'src/api';
import { escapeODataValue } from 'src/lib/oData/escapeODataValue';

export interface GetRevenueAnalyticsOptions {
    /**
     * Limit the rows to a single provider. Omitted (or empty) loads the
     * aggregate across every provider the caller can see.
     */
    providerId?: string;
    /**
     * Additional OData `$filter` predicate over the numeric row fields
     * (`quantity`, `averagePrice`, `totalCost`, `currentPrice`), and-ed with
     * the provider clause, e.g. `(quantity gt 100) and (totalCost le 5000)`.
     */
    filter?: string;
    /** Zero-based starting row, sent as OData `$skip`. Omitted (with `top`) when searching a symbol. */
    skip?: number;
    /** Page size, sent as OData `$top`. Omitted (with `skip`) when searching a symbol. */
    top?: number;
    /**
     * OData `$orderby` expression over the row shape (`providerId`,
     * `symbol`, `quantity`, `averagePrice`, `totalCost`, `currentPrice`),
     * e.g. `totalCost desc`. Omitted uses the backend default sort
     * (provider, then cost descending).
     */
    orderby?: string;
    /**
     * Exact-match symbol filter. When set, the backend scopes the result
     * to that symbol and paging is bypassed.
     */
    symbol?: string;
}

/**
 * Fetch one page of per-symbol revenue analytics and map the response
 * onto the table's raw row shape. Each row keeps its `providerId`; the
 * display name is resolved later against the loaded providers list, since
 * the aggregate view spans providers.
 *
 * Served by `GET /v1/orders/summary/by-symbol`, driven by OData over the
 * row shape: a provider selection travels as a `providerId eq …` `$filter`
 * (and-ed with any column `filter`), sorting as `$orderby`, and paging as
 * `$skip`/`$top`. `inventory` is
 * optional per row, so the SOD figures are left `null` when the backend
 * has no start-of-day data (the table renders those as a dash).
 */
export const getRevenueAnalytics = async (
    options: GetRevenueAnalyticsOptions = {},
): Promise<RevenueAnalyticsRawRow[]> => {
    const { providerId, filter, skip, top, orderby, symbol } = options;
    const clauses = [providerId ? `providerId eq '${escapeODataValue(providerId)}'` : undefined, filter].filter(
        (clause): clause is string => Boolean(clause),
    );
    const { data } = await axios.get<OrderSummaryResponse>('/v1/orders/summary/by-symbol', {
        params: {
            $filter:
                clauses.length === 0
                    ? undefined
                    : clauses.length === 1
                      ? clauses[0]
                      : clauses.map((clause) => `(${clause})`).join(' and '),
            $orderby: orderby,
            $skip: skip,
            $top: top,
            symbol,
        },
    });
    return data.rows.map((row) => ({
        providerId: row.providerId,
        symbol: row.symbol,
        qty: row.quantity,
        avgPrice: row.averagePrice,
        totalCost: row.totalCost,
        curPrice: row.currentPrice,
        fairValue: row.fairValue,
        sodInventory: row.inventory?.startOfDay ?? null,
        remainingInventory: row.inventory?.remaining ?? null,
    }));
};
